import { credentialService } from './credential-service'
import {
  supplierRepository,
  SupplierRepository,
} from '../repositories/supplier-repository'
import { supplierAuditRepository, supplierLogRepository } from '../repositories/supplier-log-repository'
import { supplierCredentialRepository } from '../repositories/supplier-credential-repository'
import type {
  Supplier,
  SupplierCredentialView,
  SupplierDashboard,
  SupplierFilters,
  SupplierInput,
  SupplierPermissions,
} from '../types'
import { DEFAULT_SUPPLIER_PERMISSIONS } from '../types'

export interface SupplierListItem extends Supplier {
  credential: SupplierCredentialView | null
  status: SupplierCredentialView['status'] | 'DISCONNECTED'
  lastLogin: string | null
}

/**
 * SupplierService — CRUD + dashboard + filters.
 */
export class SupplierService {
  private permissions: SupplierPermissions = { ...DEFAULT_SUPPLIER_PERMISSIONS }
  private readonly repository: SupplierRepository

  constructor(repository: SupplierRepository = supplierRepository) {
    this.repository = repository
  }

  setPermissions(patch: Partial<SupplierPermissions>): SupplierPermissions {
    this.permissions = { ...this.permissions, ...patch }
    return this.permissions
  }

  getPermissions(): SupplierPermissions {
    return { ...this.permissions }
  }

  assertPermission(action: keyof SupplierPermissions): void {
    if (!this.permissions[action]) throw new Error(`Permissão negada: ${action}`)
  }

  async ensureReady(): Promise<void> {
    this.repository.list()
    await credentialService.ensureSeedCredentials()
  }

  list(filters: SupplierFilters = {}): SupplierListItem[] {
    this.assertPermission('view')
    return this.repository
      .list()
      .map((supplier) => {
        const credential = credentialService.toView(
          credentialService.getBySupplierId(supplier.id),
        )
        return {
          ...supplier,
          credential,
          status: credential?.status ?? 'DISCONNECTED',
          lastLogin: credential?.lastLogin ?? null,
        }
      })
      .filter((item) => {
        if (filters.activeOnly && !item.active) return false
        if (filters.apiOnly && !item.supportsApi) return false
        if (filters.automationOnly && !item.supportsAutomation) return false
        if (filters.connectedOnly && item.status !== 'CONNECTED') return false
        if (filters.status && filters.status !== 'ALL' && item.status !== filters.status) return false
        if (filters.query) {
          const q = filters.query.toLowerCase()
          const hay = `${item.name} ${item.code} ${item.website ?? ''}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
  }

  getById(id: string): SupplierListItem | null {
    this.assertPermission('view')
    return this.list().find((s) => s.id === id) ?? null
  }

  getByCode(code: string): SupplierListItem | null {
    this.assertPermission('view')
    return this.list().find((s) => s.code === code) ?? null
  }

  create(input: SupplierInput): Supplier {
    this.assertPermission('edit')
    const created = this.repository.create(input)
    supplierLogRepository.add(created.id, 'supplier.created', `Fornecedor ${created.name} criado`, 'success')
    supplierAuditRepository.add(created.id, 'supplier', '', created.name)
    return created
  }

  update(id: string, patch: SupplierInput): Supplier {
    this.assertPermission('edit')
    const previous = this.repository.findById(id)
    if (!previous) throw new Error('Fornecedor não encontrado')
    const next = this.repository.update(id, patch)
    for (const field of ['name', 'code', 'website', 'active'] as const) {
      const before = String(previous[field] ?? '')
      const after = String(next[field] ?? '')
      if (before !== after) {
        supplierAuditRepository.add(id, field, before, after)
      }
    }
    supplierLogRepository.add(id, 'supplier.updated', `Fornecedor ${next.name} atualizado`, 'success')
    return next
  }

  remove(id: string): void {
    this.assertPermission('delete')
    supplierCredentialRepository.removeBySupplierId(id)
    this.repository.remove(id)
    supplierLogRepository.add(id, 'supplier.deleted', 'Fornecedor removido', 'warning')
  }

  dashboard(filters: SupplierFilters = {}): SupplierDashboard {
    const items = this.list({ ...filters, activeOnly: false })
    return {
      total: items.length,
      connected: items.filter((i) => i.status === 'CONNECTED').length,
      disconnected: items.filter((i) => i.status === 'DISCONNECTED').length,
      needLogin: items.filter(
        (i) => i.status === 'DISCONNECTED' || i.status === 'INVALID_CREDENTIAL' || i.status === 'ERROR',
      ).length,
    }
  }

  logs(supplierId: string) {
    this.assertPermission('view')
    return supplierLogRepository.list(supplierId)
  }

  audit(supplierId: string) {
    this.assertPermission('view')
    return supplierAuditRepository.list(supplierId)
  }
}

export const supplierService = new SupplierService()
